<template>
  <div class="import-bar" role="region" aria-label="Import preview">
    <div class="import-bar__file" :title="fileName">{{ fileName }}</div>

    <div class="import-bar__chips">
      <span
        v-if="diff.counts.decreased > 0"
        class="import-bar__chip import-bar__chip--decreased"
        title="These weeks lose capacity — check the plan with the partner before approving"
      >
        ↓ {{ diff.counts.decreased }} decrease{{ diff.counts.decreased === 1 ? '' : 's' }}
      </span>
      <span v-if="diff.counts.added > 0" class="import-bar__chip import-bar__chip--new">{{ diff.counts.added }} new</span>
      <span v-if="diff.counts.changed > 0" class="import-bar__chip import-bar__chip--changed">{{ diff.counts.changed }} changed</span>
      <span v-if="diff.counts.matched > 0" class="import-bar__chip import-bar__chip--matched">{{ diff.counts.matched }} match</span>
      <span
        v-if="diff.counts.attention > 0"
        class="import-bar__chip import-bar__chip--attention"
        title="These lines can't be previewed and will not be applied — use Open full import to reconcile them"
      >
        {{ diff.counts.attention }} needs attention
      </span>
      <span v-if="excludedCount > 0" class="import-bar__chip import-bar__chip--excluded">{{ excludedCount }} excluded</span>
    </div>

    <!-- Empty-actionable variant (spec §3/§4.1): the file parsed cleanly but every value already
         matches the calendar (or every proposed cell was excluded by hand) and nothing needs
         attention — the bar still appears, with the chips row above showing what was actually
         checked (e.g. a "N match" chip for a fully-matching file), so approving/discarding
         doesn't feel like it silently did nothing. -->
    <p v-if="isEmpty" class="import-bar__empty">{{ emptyMessage }}</p>

    <div class="import-bar__actions">
      <button type="button" class="import-bar__btn import-bar__btn--ghost" :disabled="approving" @click="emit('discard')">Discard</button>
      <template v-if="!isEmpty">
        <button v-if="diff.counts.attention > 0" type="button" class="import-bar__btn import-bar__btn--secondary" :disabled="approving" @click="emit('openFullImport')">
          Open full import
        </button>
        <button type="button" class="import-bar__btn import-bar__btn--primary" :disabled="approving || diff.cells.length === 0" @click="emit('approve')">
          {{ approving ? 'Approving…' : 'Approve import' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import type { ImportDiff } from '@/utils/importPreview';

  const props = defineProps<{
    fileName: string;
    diff: ImportDiff;
    excludedCount: number;
    approving: boolean;
  }>();

  const emit = defineEmits<{
    discard: [];
    openFullImport: [];
    approve: [];
  }>();

  // Empty-actionable variant (see the template's <p v-if="isEmpty">) — actionable meaning
  // nothing left to apply, not that the diff is empty outright: excluding every proposed cell by
  // hand lands here too (diff.cells goes to 0), but that's a different story for the user than a
  // genuinely matching file — see emptyMessage.
  const isEmpty = computed(() => props.diff.cells.length === 0 && props.diff.counts.attention === 0);

  const emptyMessage = computed(() => (props.excludedCount > 0 ? 'No changes left to apply' : 'Everything in this file already matches the calendar'));
</script>

<style scoped>
  /* Pinned below MainLayout's own sticky top bar (its own sticky top: 0, ~3.25rem tall — see
     MainLayout.vue's header) so the two never overlap. The app shell scrolls the window itself
     (no ancestor between here and the viewport sets its own overflow), so a plain top offset is
     enough for this to actually stick — no scroll-container gymnastics needed. */
  .import-bar {
    position: sticky;
    top: 3.5rem;
    z-index: 45; /* above the grid content (z-index 10-30) it sits over once stuck */
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    gap: 0.875rem;
    box-sizing: border-box;
    padding: 0.75rem 1.125rem;
    border-radius: 0.75rem;
    background: #171c24;
    box-shadow:
      0 12px 32px rgb(0 0 0 / 35%),
      0 0 0 1px rgb(255 255 255 / 6%);
    color: rgb(226 232 240);
    font-size: 0.8125rem;
  }

  .import-bar__file {
    overflow: hidden;
    max-width: 16rem;
    color: rgb(148 163 184);
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .import-bar__empty {
    margin: 0;
    color: rgb(226 232 240);
  }

  .import-bar__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .import-bar__chip {
    padding: 0.1875rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }

  /* The one chip meant to interrupt an approval, so unlike the others (translucent tints) this
     is a solid fill plus an outer glow — legible on the bar's dark background and louder than
     --changed at a glance. */
  .import-bar__chip--decreased {
    background: rgb(220 38 38);
    color: white;
    box-shadow:
      0 0 0 1px rgb(248 113 113 / 60%),
      0 0 12px rgb(220 38 38 / 55%);
  }

  .import-bar__chip--new {
    background: rgb(34 197 94 / 18%);
    color: rgb(134 239 172);
  }

  .import-bar__chip--changed {
    background: rgb(245 158 11 / 18%);
    color: rgb(252 211 77);
  }

  .import-bar__chip--matched {
    background: rgb(148 163 184 / 14%);
    color: rgb(148 163 184);
  }

  .import-bar__chip--attention {
    background: rgb(148 163 184 / 20%);
    color: rgb(203 213 225);
  }

  .import-bar__chip--excluded {
    background: rgb(100 116 139 / 25%);
    color: rgb(203 213 225);
  }

  .import-bar__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .import-bar__btn {
    border: 1px solid transparent;
    border-radius: 0.5rem;
    padding: 0.4375rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease;
    white-space: nowrap;
  }

  .import-bar__btn--ghost {
    background: transparent;
    border-color: rgb(71 85 105);
    color: rgb(226 232 240);
  }

  .import-bar__btn--ghost:hover {
    background: rgb(255 255 255 / 6%);
  }

  .import-bar__btn--secondary {
    background: rgb(255 255 255 / 8%);
    color: rgb(226 232 240);
  }

  .import-bar__btn--secondary:hover {
    background: rgb(255 255 255 / 14%);
  }

  .import-bar__btn--primary {
    background: rgb(37 99 235);
    color: white;
  }

  .import-bar__btn--primary:hover:not(:disabled) {
    background: rgb(29 78 216);
  }

  .import-bar__btn--primary:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 640px) {
    .import-bar__file {
      max-width: 100%;
    }
  }
</style>
